import {getDOMNode, tryCall} from '@esgi/ui-kit/utils';
import React, {CSSProperties, PureComponent} from 'react';
import {isArray} from 'underscore';
import {Transition, TransitionProps} from './transition/transition';
import {AnimatableElement, AnimationState} from './types';

function asArray<T>(source: T | T[]) {
	if (isArray(source)) {
		return source;
	}
	return [source];
}

type TimingFunction =
	'ease' |
	'ease-in' |
	'ease-out' |
	'ease-in-out' |
	'linear' |
	'step-start' |
	'step-end' |
	string;

type AllowProperty = keyof Omit<CSSProperties, 'transition' | 'transitionProperty' | 'transitionDuration' | 'transitionTimingFunction'>;
export type PropertyFlowDescription = {
	name: AllowProperty,
	from: string,
	to: string,
	initial?: string,
	duration: number,
	timingFunction: TimingFunction,
};

interface Props {
	flow: PropertyFlowDescription | PropertyFlowDescription[];
}

class State {
	animationState: AnimationState;
}

export class StyleAnimation extends PureComponent<Props & Omit<TransitionProps, 'duration'>, State> {

	private getMax(source: PropertyFlowDescription | PropertyFlowDescription[]): number {
		return asArray(source).map(s => s.duration).sort().reverse()[0];
	}

	public override componentDidUpdate(prevProps: Readonly<Props>) {
		if (prevProps.flow !== this.props.flow) {
			const node = getDOMNode(this);
			this.applyStyles(this.state.animationState, node);
		}
	}

	private applyStyles = (state: AnimationState, ref: AnimatableElement): void => {
		let scenario: 'from' | 'to' | 'initial';
		switch (state) {
			case 'enter': {
				scenario = 'to';
				break;
			}
			case 'exit': {
				scenario = 'from';
				break;
			}
			case 'initial': {
				scenario = 'initial';
				break;
			}
		}
		this.applyScenario(scenario, ref);
		this.setState({animationState: state});
		tryCall(this.props.beforeStateChanged, state, ref);
	};

	private applyScenario(state: 'from' | 'to' | 'initial', element: AnimatableElement): void {
		let properties = [];
		let duration = [];
		let timingFunctions = [];

		for (let property of asArray(this.props.flow)) {
			// @ts-ignore
			element.style[property.name] = property[state];

			properties.push(property.name);
			duration.push(property.duration);
			timingFunctions.push(property.timingFunction);
		}

		if (properties.length) {

			element.style.transitionProperty = properties.join(', ');
			if (state === 'initial') {
				element.style.transitionDuration = '';
			} else {
				element.style.transitionDuration = duration.map(d => d.toString() + 'ms').join(', ');
			}
			element.style.transitionTimingFunction = timingFunctions.join(', ');
		}
	}

	public override render() {
		const duration = this.getMax(this.props.flow);
		const {beforeStateChanged, ...props} = this.props;

		return <Transition {...props}
		                   duration={duration}
		                   beforeStateChanged={this.applyStyles}>
			{this.props.children}
		</Transition>;
	}
}
